/**
 * TriggerModal — web-side trigger UX for a deployed workflow.
 *
 * One modal, three sections:
 *   1. Integrations list — the shared <IntegrationsConnectList> component
 *      (same one the Settings tab uses), self-fetching per-workflow status
 *      and rendering Connect/Connected rows. Doesn't block trigger.
 *   2. Input form (SchemaForm reads workflow.graph.stateSchema).
 *   3. Submit → POST /projects/:id/workflows/:type/trigger.
 *
 * On 202 we navigate to /projects/:id/pipelines/:type/runs/:jobId BEFORE
 * closing the modal — the Activity tab's existing polling kicks in and
 * the user sees the new execution appear under the closing-modal animation.
 *
 * Industrial decisions:
 *  - Server is the authoritative validator (workflow-input-validator +
 *    Zod inside the runner). The client only does required-field gating
 *    so users don't waste a network round-trip on obviously-empty submits.
 *  - 400 validation errors come back as { validationErrors[], missing[],
 *    typeMismatches[], hint }. We splat them onto the form via serverErrors.
 *  - 429 (quota) is rendered as a top-banner, not as a per-field error —
 *    it's a billing concern, not an input problem.
 *  - idempotencyKey is auto-generated per modal-open via crypto.randomUUID(),
 *    sent with the trigger so a double-click can't spawn a second execution.
 *  - Unified gate: same body shape ({ input }) and same endpoint as the
 *    CLI (`zibby workflow trigger`) and cloud sub-graph dispatches. The
 *    web is just another client behind the same boundary.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { closeOutline, chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import api from '../../services/api';
import IntegrationsConnectList from '../../components/shared/IntegrationsConnectList/IntegrationsConnectList';
import PillButton from '../../components/shared/PillButton';
import JsonInput from '../../components/shared/SchemaForm/JsonInput';
import { detectUnion, isAdvancedField, isTriggerHidden } from '../../components/shared/SchemaForm/schema-example';
import './TriggerModal.css';

/**
 * schemaHasAdvanced — true iff the schema declares at least one ADVANCED-class
 * top-level property that is NOT required (required advanced fields stay
 * inline, so they don't justify showing the "Advanced" expander on their own).
 * Self-host / comment_reply-only fields are hidden entirely, never "advanced".
 */
function schemaHasAdvanced(schema) {
  if (!schema?.properties) return false;
  const required = new Set(Array.isArray(schema.required) ? schema.required : []);
  return Object.entries(schema.properties).some(([name, def]) => {
    if (isTriggerHidden(def)) return false;
    if (!isAdvancedField(def)) return false;
    const isReq = required.has(name) && def?.default === undefined;
    return !isReq;
  });
}

function buildServerErrorMap(payload) {
  // workflow-trigger.js returns:
  //   missing:        ['fieldA', 'fieldB']
  //   typeMismatches: [{ path: 'fieldC', expected: 'number', got: 'string' }, …]
  //   validationErrors: catch-all if more granular
  const out = {};
  for (const path of payload?.missing || []) {
    out[path] = 'Required';
  }
  for (const t of payload?.typeMismatches || []) {
    out[t.path] = `Expected ${t.expected}${t.got ? ` (got ${t.got})` : ''}`;
  }
  for (const e of payload?.validationErrors || []) {
    if (e.path && !out[e.path]) out[e.path] = e.message || e.kind || 'Invalid';
  }
  return out;
}

export default function TriggerModal({
  isOpen,
  onClose,
  pipeline,                // workflow row from GET /projects/:id/workflows/:type
  projectId,
  workflowType,
  onTriggered,             // (jobId) => void  — caller usually navigates
}) {
  const [input, setInput] = useState({});
  const [formOk, setFormOk] = useState(true);
  const [clientErrors, setClientErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const [topError, setTopError] = useState(null);
  // Typed error for the agent-key preflight (`code: 'AGENT_KEY_MISSING'`).
  // When set, the modal renders a dedicated block with a link to the
  // project's AI Settings page, instead of dumping the raw 400 message.
  // Shape: { aiAgent, requiredEnv, projectId, workflowUuid }
  const [agentKeyError, setAgentKeyError] = useState(null);
  const idempotencyKeyRef = useRef(null);

  // Three-schema model: `graph.inputSchema` is the slice the user
  // supplies at trigger time (workspace/githubToken/etc. live on
  // `graph.contextSchema` and are runner-injected, NOT rendered here).
  // Deploy-time `sanitizeGraphConfig` stamps inputSchema on every row,
  // so we just read it directly — no legacy fallback.
  // Schema + source are derived from `pipeline.graph` directly, so no
  // setters needed — they update naturally when the modal opens with a
  // new pipeline prop. (Earlier versions fetched these async; that
  // legacy path is gone now that deploy-time sanitization stamps both
  // on every workflow row up front.)
  const triggerSchema = pipeline?.graph?.inputSchema || null;
  const schemaSourceField = pipeline?.graph?.inputSchema ? 'inputSchema' : null;
  const [schemaLoading] = useState(false);
  const workflowUuid = pipeline?.uuid;

  // ── Multi-scenario (discriminated-union) detection ──────────────────────────
  // A union input schema (z.discriminatedUnion on `trigger`/`scenario`/…) renders
  // as TABS — one per variant, each showing only that variant's fields + its own
  // seeded payload. A FLAT schema returns null here and renders exactly as today.
  const union = useMemo(() => detectUnion(triggerSchema), [triggerSchema]);
  const [activeVariant, setActiveVariant] = useState(0);
  // The schema actually fed to JsonInput: the active variant's branch schema for
  // a union, else the flat schema unchanged.
  const effectiveSchema = union ? union.variants[activeVariant]?.schema || triggerSchema : triggerSchema;

  // ── Advanced-fields expander ────────────────────────────────────────────────
  // Collapsed by default. Only offered when the EFFECTIVE schema has a
  // non-required advanced field (else there's nothing to reveal).
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasAdvanced = useMemo(() => schemaHasAdvanced(effectiveSchema), [effectiveSchema]);
  // A union variant carries the discriminator as a `const`; a saved defaultInput
  // for a flat schema must NOT leak across variants, so seed from defaultInput
  // ONLY for the flat (non-union) case — each tab seeds purely from its branch.
  const seedDefault = union ? undefined : pipeline?.defaultInput;

  // Reset on open. New idempotency key per modal-open so a successful
  // trigger → close → reopen → trigger is two distinct executions.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line no-undef -- globalThis is ES2020 standard; CRA's eslint env predates it
    idempotencyKeyRef.current = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setInput({});
    setServerErrors({});
    setClientErrors({});
    setQuotaError(null);
    setTopError(null);
    setActiveVariant(0);
    setShowAdvanced(false);
  }, [isOpen]);

  // Schema comes straight off pipeline.graph (initialized above) — no
  // fetch needed. The deploy pipeline stamps inputSchema + contextSchema
  // + stateSchema on the workflow row at deploy time (workflow-service.js
  // sanitizeGraphConfig), so the row IS the source of truth.

  // Esc to close (unless submitting).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  const handleJsonChange = useCallback((parsedValue, { ok, error }) => {
    setInput(parsedValue || {});
    setFormOk(ok);
    setClientErrors(error ? { _payload: error } : {});
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!formOk) return;

    setSubmitting(true);
    setQuotaError(null);
    setTopError(null);
    setServerErrors({});
    setAgentKeyError(null);

    try {
      const resp = await api.post(
        `/projects/${projectId}/workflows/${workflowType}/trigger`,
        {
          input,
          idempotencyKey: idempotencyKeyRef.current,
        },
      );
      const jobId = resp?.jobId;
      if (!jobId) {
        setTopError('Trigger accepted but no jobId returned — please refresh and check Activity.');
        return;
      }
      // Navigate FIRST, then onTriggered is called for state-cleanup.
      // The caller decides what to do (typically navigate to the run page).
      onTriggered?.(jobId);
    } catch (err) {
      // api.js throws Error with .status + .body for non-2xx.
      const status = err?.status;
      const body = err?.body || err?.data || {};
      if (status === 400 && body.code === 'AGENT_KEY_MISSING') {
        setAgentKeyError({
          aiAgent: body.aiAgent,
          requiredEnv: body.requiredEnv,
          projectId: body.projectId,
          workflowUuid: body.workflowUuid,
        });
      } else if (status === 400) {
        setServerErrors(buildServerErrorMap(body));
        if (body.hint) setTopError(body.hint);
      } else if (status === 429) {
        setQuotaError(body.quotaInfo || { message: body.error || 'Workflow execution quota exceeded' });
      } else if (status === 403) {
        setTopError('You do not have permission to trigger this workflow.');
      } else {
        setTopError(body.error || err.message || 'Failed to trigger workflow.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, formOk, projectId, workflowType, input, onTriggered]);

  if (!isOpen) return null;

  return (
    <div
      className="trigger-modal-overlay"
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        className="trigger-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trigger-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="trigger-modal-head">
          <h2 id="trigger-modal-title" className="trigger-modal-title">
            Trigger {pipeline?.displayName || workflowType}
          </h2>
          <button
            type="button"
            className="trigger-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="trigger-modal-body">
          {/* ── Quota / top error banner ─────────────────────────────── */}
          {quotaError && (
            <div className="trigger-modal-banner trigger-modal-banner-warn">
              <strong>Agent execution quota exceeded</strong>
              {quotaError.used !== undefined && quotaError.limit !== undefined && (
                <span> — {quotaError.used} / {quotaError.limit} this period.</span>
              )}{' '}
              <a href="/settings/billing" className="trigger-modal-banner-link">Upgrade plan →</a>
            </div>
          )}
          {topError && !quotaError && !agentKeyError && (
            <div className="trigger-modal-banner trigger-modal-banner-error">
              {topError}
            </div>
          )}
          {/* Agent-key preflight failure — backend rejected the trigger
              because neither the workflow's env nor the project's AI
              Settings holds a key for the resolved agent. Surface a
              clear actionable block: name the agent + the env key it
              needs, link to the project's settings page, mention the
              CLI alternative. Beats letting the trigger spawn a
              Fargate task that just crashes "Authentication required". */}
          {agentKeyError && (
            <div className="trigger-modal-banner trigger-modal-banner-error">
              <strong>Missing {agentKeyError.requiredEnv} for {agentKeyError.aiAgent} agent.</strong>
              <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                Set it one of two ways:
                <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  <li>
                    Open{' '}
                    <a
                      href={`/projects/${agentKeyError.projectId || projectId}/settings?section=ai`}
                      className="trigger-modal-banner-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Project Settings → AI Settings →
                    </a>{' '}
                    and paste your <code>{agentKeyError.aiAgent}</code> API key.
                  </li>
                  <li>
                    Or per-workflow:{' '}
                    <code>zibby workflow env {agentKeyError.workflowUuid || workflowUuid || '<uuid>'} set {agentKeyError.requiredEnv}=&lt;your-key&gt;</code>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* ── Integrations ───────────────────────────────────────────
              Reuse the SAME component the Settings tab renders so the two
              surfaces are byte-for-byte consistent. It self-fetches status
              from workflowUuid and owns its own header/list/loading/empty
              states (required + optional rows with Connect buttons). We
              just give it a minimal wrapping section for vertical spacing,
              matching how Settings drops it in. Trigger is NOT gated on
              this — connecting is encouraged, not enforced. */}
          {workflowUuid && (
            <section className="trigger-modal-section">
              <IntegrationsConnectList workflowUuid={workflowUuid} />
            </section>
          )}

          {/* ── Input (JSON textarea, schema-driven default) ─────────────
              Seed precedence:
                1. pipeline.defaultInput  — user's saved per-deployment
                   default (PATCH /workflows/:uuid {defaultInput})
                2. schema-derived example — from inputSchema defaults
              Nothing hardcoded — every template gets its own auto-
              generated payload that the user edits in place AND can
              persist via the "Save as default" button below. */}
          <section className="trigger-modal-section">
            {/* ── Scenario tabs (discriminated-union schemas only) ──────────
                One tab per union variant. Switching tabs swaps the schema fed
                to JsonInput, which re-seeds the payload (pinning the
                discriminator const for that variant) and shows only that
                variant's fields. FLAT schemas render no tabs. */}
            {union && union.variants.length > 1 && (
              <div className="trigger-modal-tabs" role="tablist" aria-label="Scenario">
                {union.variants.map((v, i) => (
                  <button
                    key={String(v.value)}
                    type="button"
                    role="tab"
                    aria-selected={i === activeVariant}
                    className={`trigger-modal-tab ${i === activeVariant ? 'is-active' : ''}`}
                    onClick={() => {
                      if (i === activeVariant) return;
                      setActiveVariant(i);
                      setShowAdvanced(false);
                      setServerErrors({});
                    }}
                    disabled={submitting}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}

            <JsonInput
              // Remount per variant so the editor re-seeds cleanly on tab switch
              // (the seed includes the variant's discriminator const).
              key={union ? `variant-${activeVariant}` : 'flat'}
              schema={effectiveSchema}
              initialValue={seedDefault}
              onChange={handleJsonChange}
              disabled={submitting || schemaLoading}
              hideInternal
              hideAdvanced={hasAdvanced && !showAdvanced}
            />

            {/* ── Advanced expander ─────────────────────────────────────────
                Advanced-class fields are collapsed out of the seed by default;
                this toggle re-seeds the editor WITH them. Required fields are
                never collapsed, so this only ever reveals optional power-knobs. */}
            {hasAdvanced && (
              <button
                type="button"
                className="trigger-modal-advanced-toggle"
                onClick={() => setShowAdvanced((s) => !s)}
                aria-expanded={showAdvanced}
                disabled={submitting}
              >
                <IonIcon icon={showAdvanced ? chevronDownOutline : chevronForwardOutline} />
                {showAdvanced ? 'Hide advanced fields' : 'Show advanced fields'}
              </button>
            )}

            {schemaSourceField === 'stateSchema' && (
              <div className="trigger-modal-banner trigger-modal-banner-warn" style={{ marginTop: 8 }}>
                <strong>Template hasn't declared an <code>inputSchema</code>.</strong>{' '}
                Showing the full runtime state — some fields below are
                injected by the runner from project config / integrations
                (workspace, githubToken, repos, …) and should be left blank
                at trigger time. Fix: export <code>inputSchema</code> from
                the template's <code>state.js</code> alongside{' '}
                <code>stateSchema</code>; rebuild + redeploy.
              </div>
            )}
            {Object.keys(serverErrors).length > 0 && (
              <div className="trigger-modal-server-errors">
                <strong>Server validation:</strong>
                <ul>
                  {Object.entries(serverErrors).map(([path, msg]) => (
                    <li key={path}><code>{path}</code> — {msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div className="trigger-modal-actions">
            <button
              type="button"
              className="trigger-modal-cancel-link"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <PillButton
              type="submit"
              loading={submitting}
              disabled={!formOk}
            >
              Trigger
            </PillButton>
          </div>

          {!formOk && clientErrors._payload && !submitting && (
            <p className="trigger-modal-hint">Fix the JSON syntax above to enable Trigger.</p>
          )}
        </form>
      </div>
    </div>
  );
}
